import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2, Globe, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE = 'https://fernandotech.com.br';

const PRESET_URLS = [
  `${SITE}/`,
  `${SITE}/comprar`,
  `${SITE}/orcamento-intelbras`,
  `${SITE}/assistencia-tecnica`,
  `${SITE}/daten`,
  `${SITE}/clientes-vip`,
  `${SITE}/llms.txt`,
  `${SITE}/llms-full.txt`,
  `${SITE}/sitemap.xml`,
];

const AdminSeo = () => {
  const [urlsText, setUrlsText] = useState(PRESET_URLS.join('\n'));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const urls = urlsText.split('\n').map(s => s.trim()).filter(Boolean);
    if (urls.length === 0) return;
    setLoading(true); setResult(null);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/indexnow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${pwd}` },
        body: JSON.stringify({ urls }),
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6" data-testid="admin-seo-tab">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-cyan-400" />
        <h3 className="text-lg font-bold text-white">SEO & Indexação Rápida</h3>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-4">
        <h4 className="text-sm font-semibold text-cyan-300 mb-2 flex items-center gap-2">
          <Globe className="w-4 h-4" />Status do SEO
        </h4>
        <ul className="text-xs text-slate-300 space-y-1">
          <li>✅ Sitemap: <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">/sitemap.xml</a></li>
          <li>✅ Robots: <a href="/robots.txt" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">/robots.txt</a></li>
          <li>✅ Mapa para IAs: <a href="/llms.txt" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">/llms.txt</a> · <a href="/llms-full.txt" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">/llms-full.txt</a></li>
          <li>✅ IndexNow chave: <code className="bg-slate-900 px-1.5 py-0.5 rounded text-cyan-300">37b775dc30a54ea687c51cf65e25278e</code></li>
          <li>✅ Schemas JSON-LD: 27 tipos (LocalBusiness, Service, Product, FAQ, Review, ...)</li>
        </ul>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-4">
        <label className="block text-sm font-semibold text-slate-200 mb-2">
          URLs para indexar (uma por linha)
        </label>
        <textarea
          value={urlsText}
          onChange={e => setUrlsText(e.target.value)}
          rows={9}
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 text-white text-sm font-mono rounded-lg outline-none focus:border-cyan-500"
          data-testid="indexnow-urls-input"
        />
        <p className="text-[11px] text-slate-500 mt-2">
          As URLs serão enviadas para Bing, Yandex e Seznam via IndexNow. O Google indexa automaticamente via sitemap.xml — não precisa enviar aqui.
        </p>
        <Button
          onClick={submit}
          disabled={loading}
          className="mt-3 bg-cyan-600 hover:bg-cyan-500 text-white"
          data-testid="indexnow-submit"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : <><Send className="w-4 h-4 mr-2" />Enviar para IndexNow</>}
        </Button>
      </div>

      {result && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-2">Resultado</h4>
          {result.error ? (
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />{result.error}
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-400">{result.submitted?.length} URL(s) enviada(s):</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {Object.entries(result.results || {}).map(([engine, r]) => (
                  <div
                    key={engine}
                    className={`flex items-center gap-2 p-3 rounded-lg border ${
                      r.ok ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
                    }`}
                    data-testid={`indexnow-result-${engine}`}
                  >
                    {r.ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      : <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                    }
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold capitalize ${r.ok ? 'text-emerald-300' : 'text-red-300'}`}>{engine}</p>
                      <p className="text-[11px] text-slate-400 truncate">HTTP {r.status} {r.error ? `· ${r.error}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Status 200/202 = aceito. 403 (Seznam) é normal — Seznam exige pré-validação manual e tem relevância só na República Tcheca.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSeo;
